// useSeats — the seat score for every deity (Update Directive 18 Oct 2026 §5).
// Daily marks come from each deity's principle log (sectorConfig
// principleProgress); situational marks from dmt_events rows with source
// 'manual' (the Used [deity] / [Malady] won buttons). Intake positions
// (dmt_state.displayed_position) are context only — they never set a seat.
import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useDataVersion } from '../live/screenContext';
import { todayKey } from './deity-progress';
import { seatFor, type SeatScore, type SituationalEvent } from './seatScore';
import { useCompassConfig } from './sectorConfig';
import { DMT_TOOLS, type DmtToolId } from './spectrum-tools';

export type SeatBoard = Record<DmtToolId, SeatScore>;

export function useSeats(userId: string | undefined): { seats: SeatBoard; loading: boolean } {
  const { config, loading: cfgLoading } = useCompassConfig(userId);
  const game = config.game;
  const [events, setEvents] = useState<Record<string, SituationalEvent[]>>({});
  const [loading, setLoading] = useState(true);
  const dataVersion = useDataVersion();

  useEffect(() => {
    if (!userId) return;
    let live = true;
    const since = new Date();
    since.setDate(since.getDate() - game.seatWindowDays - 1);
    supabase
      .from('dmt_events')
      .select('id, tool_id, occurred_at, grade')
      .eq('user_id', userId)
      .eq('source', 'manual')
      .gte('occurred_at', since.toISOString())
      .then(({ data }) => {
        if (!live) return;
        const by: Record<string, SituationalEvent[]> = {};
        for (const r of (data ?? []) as { id: string; tool_id: string; occurred_at: string; grade: number }[]) {
          (by[r.tool_id] ??= []).push({ id: String(r.id), occurredAt: r.occurred_at, grade: r.grade });
        }
        setEvents(by);
        setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [userId, game.seatWindowDays, dataVersion]);

  const seats = useMemo(() => {
    const today = todayKey();
    return Object.fromEntries(
      DMT_TOOLS.map((t) => [t.id, seatFor(config.principleProgress?.[t.id], events[t.id] ?? [], today, game, t.id)]),
    ) as SeatBoard;
  }, [config.principleProgress, events, game]);

  return { seats, loading: loading || cfgLoading };
}
